/**
 * Normalises a string for Autocomplete matching.
 *
 * School names are full of punctuation an admin never types: "King's College School",
 * "St. Paul's", "King Edward VI Grammar School, Chelmsford". MUI's default filter is a
 * plain substring test, so typing "kings" missed every apostrophised "King's ..." school
 * and the list looked as though the school did not exist.
 *
 * Drops diacritics and apostrophes (straight and curly), then folds every other
 * punctuation run to a single space so word boundaries still matter.
 */
export const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/['‘’ʼ`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const defaultLabel = (option) =>
  typeof option === "string" ? option : option?.name ?? option?.label ?? "";

/**
 * Builds an Autocomplete `filterOptions` that ignores punctuation and accents on both
 * sides. Every whitespace-separated term the user typed must appear somewhere in the
 * option, so "kings college" and "college kings" both find "King's College School".
 *
 * @param {(option: any) => string} [getLabel] reads the searchable text off an option.
 */
export const makeLooseFilterOptions = (getLabel = defaultLabel) => (options, state) => {
  const terms = normalizeSearchText(state?.inputValue).split(" ").filter(Boolean);
  if (terms.length === 0) return options;
  return options.filter((option) => {
    const haystack = normalizeSearchText(getLabel(option));
    return terms.every((term) => haystack.includes(term));
  });
};

/** Ready-made filter for options shaped `{ name }` / `{ label }` or plain strings. */
export const filterOptionsLoose = makeLooseFilterOptions();
